/**
 * beneficiaryCoreEdit — CHG-0186 (Bill 2026-11-04, ruling "option B").
 *
 * A beneficiary linked to an EXISTING customer (cifId) is that customer's Core
 * Director record. When the officer changes a field Core holds — first, middle
 * or last name, date of birth, phone — the edit goes through the same Change in
 * Contact step an owner's edit does (useProfileChange: compare with Core →
 * confirm with documentation + account waterfall → CustMod + notes + insider
 * scan). No deferral (Bill 2026-11-04): Discard puts Core's current values back
 * on the row, and when Core cannot be compared nothing is saved. Either way the
 * beneficiary designation documents print what Core holds. Untouched linked rows
 * behave as before. Only in profileChange.mode = commit-time (admin alternative)
 * is the edit kept as `edited` for the commit to write
 * (CartCommitService.UpdateLinkedBeneficiaryCifsAsync).
 *
 * State rides on the entry as `coreProfile` (server: LinkedBeneficiaryCoreEdit):
 *   linked        Core's values when the row was linked / last confirmed
 *   edited        commit-time mode only: a Core-held field the commit must write
 *   phoneSlot     which Core phone (cell / home / business) the one phone field is
 *   profileChange the applied key once Change in Contact wrote it
 *
 * benefToProfilePerson MUST mirror LinkedBeneficiaryCoreEdit.PersonNode on the
 * server (same keys), or the commit cannot recognise an applied change.
 */
import type { ProfileChangeMode } from '@retail-ops/shared-types';
import type { RaoPerson } from '../types';
import { benefIsEntity } from './beneficiaryRequirements';

export type BenefPhoneSlot = 'phoneCell' | 'phoneHome' | 'phoneBus';

export interface BenefCoreValues {
  firstName: string;
  middleName: string;
  lastName: string;
  dob: string;
  /** Digits only. */
  phone: string;
}

export interface BenefCoreProfile {
  linked?: BenefCoreValues;
  edited?: boolean;
  phoneSlot?: BenefPhoneSlot;
  profileChange?: {
    appliedAt?: string;
    appliedKey?: string;
    maintActionId?: string | number | null;
    authMethod?: string;
  };
}

/** The fields of a beneficiary row this module reads (structural, so the editor's BenefDraft fits). */
export interface BenefCoreFields {
  cifId?: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  dob?: string;
  phone?: string;
  relationship: string;
  coreProfile?: BenefCoreProfile;
}

const t = (s: string | undefined): string => (s ?? '').trim();

/** The Core-held values on the row, normalised for comparison. */
export function benefCoreValues(b: Pick<BenefCoreFields, 'firstName' | 'middleName' | 'lastName' | 'dob' | 'phone'>): BenefCoreValues {
  return {
    firstName: t(b.firstName),
    middleName: t(b.middleName),
    lastName: t(b.lastName),
    dob: t(b.dob).slice(0, 10),
    phone: (b.phone ?? '').replace(/\D/g, ''),
  };
}

function sameValues(a: BenefCoreValues, b: BenefCoreValues): boolean {
  const n = (s: string): string => s.toLowerCase().replace(/\s+/g, ' ');
  return n(a.firstName) === n(b.firstName)
    && n(a.middleName) === n(b.middleName)
    && n(a.lastName) === n(b.lastName)
    && a.dob.replace(/\D/g, '') === b.dob.replace(/\D/g, '')
    && a.phone === b.phone;
}

/** A person linked to an existing customer. Trust / charity beneficiaries (no person name in
 *  Core) keep today's behaviour. */
export function benefIsLinkedIndividual(b: BenefCoreFields): boolean {
  return t(b.cifId).length > 0 && !benefIsEntity(b) && t(b.firstName).length > 0;
}

/** Note shown on a locked linked row. */
export const BENEF_CORE_LOCKED_NOTE = 'On file in Core Director. Change it in Customer Management.';

/**
 * A linked trust / charity / business customer: its Core-held name and phone are
 * locked in the beneficiary editor (CHG-0186). Change in Contact is a person
 * ceremony, so an edit here would go nowhere; the record is changed in Customer
 * Management. "Business" = linked with no person first name on file.
 */
export function benefCoreLocked(b: BenefCoreFields): boolean {
  if (t(b.cifId).length === 0) return false;
  if (benefIsEntity(b)) return true;
  const linked = b.coreProfile?.linked;
  return linked !== undefined && linked.firstName.length === 0 && linked.lastName.length > 0;
}

/**
 * True when Save must run the Change in Contact step for this row: a linked
 * individual whose Core-held values differ from what Core held at link time (or
 * whose link predates this record, so only Core can say).
 */
export function benefNeedsCoreCheck(b: BenefCoreFields): boolean {
  if (!benefIsLinkedIndividual(b)) return false;
  const linked = b.coreProfile?.linked;
  if (!linked) return true;
  return !sameValues(benefCoreValues(b), linked);
}

/** Record Core's values at link time (the link + CustInq hydration fill the row from Core). */
export function linkSnapshot(b: Pick<BenefCoreFields, 'firstName' | 'middleName' | 'lastName' | 'dob' | 'phone'>): BenefCoreProfile {
  return { linked: benefCoreValues(b), edited: false };
}

/**
 * Which Core phone the row's single phone field stands for: the slot that held the
 * phone shown at link time; else the first Core holds (cell, home, business — the
 * order the editor fills from); else cell, the owner path's fallback.
 */
export function pickPhoneSlot(
  core: { phoneCell?: string; phoneHome?: string; phoneBus?: string },
  linkedPhone?: string,
): BenefPhoneSlot {
  const d = (s?: string): string => (s ?? '').replace(/\D/g, '');
  const slots: BenefPhoneSlot[] = ['phoneCell', 'phoneHome', 'phoneBus'];
  const was = d(linkedPhone);
  if (was) {
    const hit = slots.find((s) => d(core[s]) === was);
    if (hit) return hit;
  }
  return slots.find((s) => d(core[s]).length > 0) ?? 'phoneCell';
}

/** The person the Change in Contact step compares with Core: the Core-held fields only. */
export function benefToProfilePerson(b: BenefCoreFields & { id?: string }, slot: BenefPhoneSlot): RaoPerson {
  const v = benefCoreValues(b);
  const person: Record<string, unknown> = {
    ...(b.id ? { id: b.id } : {}),
    role: 'beneficiary',
    mode: 'existing',
    cifId: t(b.cifId),
    firstName: v.firstName,
    lastName: v.lastName,
    fullName: `${v.firstName} ${v.lastName}`.trim(),
    ...(v.middleName ? { middleName: v.middleName } : {}),
    ...(v.dob ? { dob: v.dob } : {}),
    ...(v.phone ? { [slot]: v.phone } : {}),
  };
  return person as unknown as RaoPerson;
}

/**
 * Fold the Change in Contact outcome back onto the row.
 *   written      → Core holds the row's values: snapshot them, stamp the applied key.
 *   discarded    → Core's current values go back on the row (name, DOB, phone in its slot).
 *   no change    → Core already held them: snapshot them.
 *   commit-time mode (no ceremony) → edited: the commit writes them.
 * (Core unreachable never reaches here: the step resolves proceed=false and the editor stays open.)
 */
export function applyCoreOutcome<T extends BenefCoreFields>(
  b: T,
  outcome: { applied: boolean; discarded?: boolean; person: RaoPerson; notice?: { tone: 'ok' | 'warn' | 'err' } },
  slot: BenefPhoneSlot,
  mode: ProfileChangeMode,
): T {
  const values = benefCoreValues(b);
  if (outcome.applied) {
    const extra = ((outcome.person as unknown as { extra?: Record<string, unknown> }).extra ?? {});
    const pc = extra['profileChange'] as BenefCoreProfile['profileChange'] | undefined;
    return { ...b, coreProfile: { linked: values, edited: false, phoneSlot: slot, ...(pc ? { profileChange: pc } : {}) } };
  }
  if (outcome.discarded) {
    const p = outcome.person as unknown as Record<string, unknown>;
    const s = (k: string): string => (typeof p[k] === 'string' ? (p[k] as string) : '');
    const back = {
      firstName: s('firstName'),
      middleName: s('middleName'),
      lastName: s('lastName'),
      dob: s('dob'),
      phone: s(slot).replace(/\D/g, ''),
    };
    return { ...b, ...back, coreProfile: { linked: benefCoreValues(back), edited: false, phoneSlot: slot } };
  }
  if (mode === 'confirm') return { ...b, coreProfile: { linked: values, edited: false, phoneSlot: slot } };
  return {
    ...b,
    coreProfile: {
      ...(b.coreProfile?.linked ? { linked: b.coreProfile.linked } : {}),
      edited: true,
      phoneSlot: slot,
    },
  };
}
